import {BaseComponent} from './base-component.js'
import {customElement, property} from 'lit/decorators.js'
import {html, css, TemplateResult, CSSResultGroup} from 'lit'

@customElement('il-image-modal')
export default class ImageModalComponent extends BaseComponent {
  static override events = {
    CLOSE_MODAL: 'closeModal',
  }

  @property({attribute: 'image-src', type: String})
  imageSrc = undefined

  @property({attribute: 'image-alt-text', type: String})
  imageAltText = undefined

  // set this as true if you need your modal to stay open when clicking on the overlay
  @property({attribute: 'stay-open-on-overlay-click', type: Boolean})
  stayOpenOnOverlayClick = false

  @property({attribute: 'is-open', type: Boolean})
  isOpen = false

  @property({type: Function})
  onClose?: () => {} = undefined

  private onCloseEventListener: (event: Event) => void = (_event) => {
    this.dispatchEvent(new CustomEvent(ImageModalComponent.events.CLOSE_MODAL))
  }

  override connectedCallback(): void {
    super.connectedCallback()

    this.addEventListener(ImageModalComponent.events.CLOSE_MODAL, this.onCloseEventListener, {
      capture: true,
    })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()

    this.removeEventListener(ImageModalComponent.events.CLOSE_MODAL, this.onCloseEventListener, {
      capture: true,
    })
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-image-modal-container {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .il-image-modal-overlay {
        position: absolute;
        inset: 0;
        background-color: var(--il-color-white-opacity-75);
      }

      .il-image-modal {
        position: relative;
        max-height: calc(100% - 2rem);
        max-width: 618px;
        background-color: var(--il-color-neutrals-0);
        box-shadow: var(--il-shadow-cool-base);
        border-radius: 12px;
        overflow: auto;
      }

      .il-image-modal__body {
        display: flex;
      }

      .il-image-modal__close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        z-index: 1;
      }
    `,
  ]

  private _closeIcon: TemplateResult = html`<svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M5.375 15.854L4.146 14.625L8.771 10L4.146 5.375L5.375 4.146L10 8.771L14.625 4.146L15.854 5.375L11.229 10L15.854 14.625L14.625 15.854L10 11.229L5.375 15.854Z"
      fill="#414159"
    />
  </svg>`

  private renderCloseButton() {
    return html`
      <div class="il-image-modal__close-button">
        <il-button
          @click=${this._onClose}
          variant="tertiary"
          .small=${true}
          .iconOnly=${true}
          data-test-id-iliad-image-modal-close-button
        >
          ${this._closeIcon}
        </il-button>
      </div>
    `
  }

  override render() {
    if (this.isOpen) {
      return html`
        <div
          class="
            ${this.baseCSSClass}
            il-image-modal-container
            ${this.isOpen ? 'il-image-modal-container--open' : ''}
          "
          part="image-modal-container"
          data-test-iliad-image-modal-container
        >
          <div
            class="il-image-modal-overlay"
            @click=${() => this._onOverlayClick()}
            data-test-iliad-image-modal-overlay
          ></div>

          <div class="il-image-modal" part="modal" data-test-iliad-image-modal>
            <div
              class="il-image-modal__body"
              part="image-modal-body"
              data-test-iliad-image-modal-body
            >
              ${this.renderCloseButton()}
              <img
                src="${this.imageSrc}"
                alt=${this.imageAltText}
                data-test-iliad-image-modal-image
              />
            </div>
          </div>
        </div>
      `
    } else {
      return html``
    }
  }

  private _onOverlayClick(): void {
    if (!this.stayOpenOnOverlayClick) {
      this._onClose()
    }
  }

  private _onClose(): void {
    this.isOpen = false
    this.onClose?.()
  }
}
